import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import type { BlogCategory } from "@/lib/blog-topics";

export type BlogPost = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  excerpt: string;
  body_md: string;
  category: BlogCategory;
  keywords: string[];
  cover_url: string | null;
  reading_time: number;
  status: "draft" | "published";
  published_at: string | null;
  created_at: string;
  updated_at: string;
};

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
}

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

// ---- Public reads ----

export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator((input: { category?: string; limit?: number } | undefined) => ({
    category: input?.category,
    limit: Math.min(input?.limit ?? 24, 60),
  }))
  .handler(async ({ data }): Promise<BlogPost[]> => {
    const sb = publicClient();
    let q = sb
      .from("blog_posts")
      .select("*")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(data.limit);
    if (data.category) q = q.eq("category", data.category);
    const { data: rows, error } = await q;
    if (error) {
      console.error("listPublishedPosts", error);
      return [];
    }
    return (rows ?? []) as BlogPost[];
  });

export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data }): Promise<BlogPost | null> => {
    const sb = publicClient();
    const { data: row, error } = await sb
      .from("blog_posts")
      .select("*")
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) {
      console.error("getPostBySlug", error);
      return null;
    }
    return (row as BlogPost) ?? null;
  });

// ---- Admin ----

export const amIAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<boolean> => {
    const { data } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    return Boolean(data);
  });

export const listAllPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BlogPost[]> => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return (data ?? []) as BlogPost[];
  });

export const generatePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { category?: BlogCategory; topic?: string; publish?: boolean } | undefined) => ({
      category: input?.category,
      topic: input?.topic,
      publish: input?.publish ?? false,
    }),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { generateBlogPost } = await import("@/lib/blog-ai.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const gen = await generateBlogPost({ category: data.category, topic: data.topic });
    const cover_url = gen.cover_path ? `/blog-cover/${gen.slug}.png` : null;
    const now = new Date().toISOString();

    const { data: inserted, error } = await supabaseAdmin
      .from("blog_posts")
      .insert({
        slug: gen.slug,
        title: gen.title,
        subtitle: gen.subtitle,
        excerpt: gen.excerpt,
        body_md: gen.body_md,
        category: gen.category,
        keywords: gen.keywords,
        cover_url,
        reading_time: gen.reading_time,
        status: data.publish ? "published" : "draft",
        published_at: data.publish ? now : null,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return inserted as BlogPost;
  });

export const updatePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string; patch: Partial<BlogPost> }) => input)
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: Record<string, any> = { ...data.patch };
    // Auto-set published_at when moving to published
    if (patch.status === "published" && !patch.published_at) {
      patch.published_at = new Date().toISOString();
    }
    delete patch.id;
    delete patch.created_at;
    delete patch.updated_at;
    const { data: row, error } = await supabaseAdmin
      .from("blog_posts")
      .update(patch as any)
      .eq("id", data.id)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as BlogPost;
  });

export const deletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => input)
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const seedInitialPosts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { count } = await supabaseAdmin
      .from("blog_posts")
      .select("*", { count: "exact", head: true });
    if ((count ?? 0) >= 6) return { ok: true, skipped: true, existing: count };

    const { generateBlogPost } = await import("@/lib/blog-ai.server");
    const { CATEGORIES } = await import("@/lib/blog-topics");
    const results: any[] = [];
    for (const category of CATEGORIES) {
      try {
        const gen = await generateBlogPost({ category });
        const cover_url = gen.cover_path ? `/blog-cover/${gen.slug}.png` : null;
        const { data, error } = await supabaseAdmin
          .from("blog_posts")
          .insert({
            slug: gen.slug,
            title: gen.title,
            subtitle: gen.subtitle,
            excerpt: gen.excerpt,
            body_md: gen.body_md,
            category: gen.category,
            keywords: gen.keywords,
            cover_url,
            reading_time: gen.reading_time,
            status: "published",
            published_at: new Date().toISOString(),
          })
          .select("id, slug")
          .single();
        if (error) throw error;
        results.push(data);
      } catch (err) {
        console.error("seed failed for", category, err);
        results.push({ category, error: String(err) });
      }
    }
    return { ok: true, results };
  });
